/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * @author Gladson Samuel S
 * DifficultTopicStyles.js
 * Styles for difficult topic components
 */

import { makeStyles } from '@material-ui/core';
import mixins from '../../../assets/styles/mixins.scss';

const useDifficultTopicStyles = makeStyles(() => ({
  reviewCardRoot: {
    backgroundColor: mixins.lightThemeBgColor2
  },

  actionsLeftContainer: {},

  reviewContentStyle: {
    display: 'flex',
    alignItems: 'stretch',
    gap: 16,

    [mixins['mlg-max']]: {
      // small screens: single column
      flexDirection: 'column'
    }
  },

  /** Question + struggling table + bottom review — one flex item so insights column can stretch to full height */
  leftColumnStack: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    boxSizing: 'border-box'
  },

  leftPanel: {
    flexShrink: 0
  },

  rightPanel: {
    flexShrink: 0,
    flexBasis: '420px',
    maxWidth: '100%',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    boxSizing: 'border-box',
    '& > *': {
      flex: 1,
      minHeight: 0
    },
    [mixins['mlg-max']]: {
      flexBasis: 'auto',
      width: '100%',
      '& > *': {
        flex: 'none'
      }
    }
  },

  bottomReviewContentStyle: (props) => ({
    display: 'grid',
    gap: 16,
    width: '100%',
    boxSizing: 'border-box',
    gridTemplateColumns: props.isPatternInAiUse
      ? 'repeat(12, 1fr)' // 4 + 8 layout
      : '1fr', // full width when no AI pattern

    [mixins['mlg-max']]: {
      gridTemplateColumns: '1fr'
    }
  }),

  swappedLeftPanel: {
    gridColumn: '1 / span 4', // width same as original rightPanel
    [mixins['mlg-max']]: {
      gridColumn: '1 / -1'
    }
  },

  swappedRightPanel: {
    gridColumn: '5 / span 8', // width same as original leftPanel
    [mixins['mlg-max']]: {
      gridColumn: '1 / -1'
    }
  },

  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    padding: 16,
    borderRadius: 16,
    background: mixins.chalkWhiteFont
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    flex: 1
  },

  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8
  },

  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    flex: 1
  },

  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    whiteSpace: 'nowrap'
  },

  flagButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6, // space between icon & text
    backgroundColor: mixins.bulkEditCtaButtonDisabledBGColor,
    color: mixins.color_text_primary,
    textTransform: 'none',
    borderRadius: 4,
    padding: '2px 4px 4px 4px',
    fontSize: mixins.fontSize16,
    fontWeight: mixins.fontWeight500,
    lineHeight: '24px',
    fontFamily: mixins.fontFamilyPlusSans,
    height: 32
  },

  flagIcon: {
    display: 'flex',
    alignItems: 'center'
  },

  reviewButton: {
    backgroundColor: mixins.chalkWhiteFont,
    color: mixins.color_text_primary,
    textTransform: 'none',
    border: `1px solid ${mixins.reviewButtonColor}`,
    fontSize: mixins.fontSize14,
    fontWeight: mixins.fontWeight500,
    borderRadius: 4,
    lineHeight: '24px',
    fontFamily: mixins.fontFamilyPlusSans,
    padding: '2px 4px 4px 4px',
    '&:hover': {
      backgroundColor: mixins.chalkWhiteFont
    }
  },

  questionRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    marginTop: 20
  },
  chapterHeader: {
    fontFamily: mixins.fontFamilyPlusSans,
    fontWeight: mixins.fontWeight400,
    fontStyle: 'normal',
    fontSize: mixins.fontSize12,
    lineHeight: mixins.lineHeight20,
    letterSpacing: 0,
    color: mixins.zambezi,
    verticalAlign: 'middle',
    marginBottom: 0
  },
  chapterSubHeader: {
    fontFamily: mixins.fontFamilyPlusSans,
    fontWeight: mixins.fontWeight400,
    fontStyle: 'normal',
    fontSize: mixins.fontSize12,
    lineHeight: '16px',
    letterSpacing: 0,
    color: mixins.zambezi,
    verticalAlign: 'middle'
  },
  question: {
    fontFamily: mixins.fontFamilyPlusSans,
    fontWeight: mixins.fontWeight500,
    fontStyle: 'normal',
    fontSize: mixins.fontSize20,
    lineHeight: '28px',
    letterSpacing: '-0.4',
    color: mixins.color_text_primary,
    verticalAlign: 'middle',
    paddingLeft: 32, // space reserved for 'Q 1:'
    textIndent: -32, // pull first line back
    wordBreak: 'break-word'
  },
  flaggedChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 8px',
    background: mixins.flaggedChipColor,
    color: mixins.color_text_primary,
    borderRadius: 6,
    fontFamily: mixins.fontFamilyPlusSans,
    fontWeight: mixins.fontWeight500,
    fontSize: mixins.fontSize12,
    lineHeight: '16px'
  },
  options: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    width: '100%',
    boxSizing: 'border-box',
    overflow: 'hidden'
  },
  optionRow: {
    display: 'grid',
    gridTemplateColumns: '24px 1fr auto',
    columnGap: 8,
    width: '100%',
    padding: '10px 12px',
    border: `1px solid ${mixins.flaggedChipColor}`,
    borderRadius: 8,
    background: mixins.chalkWhiteFont,
    boxSizing: 'border-box'
  },
  optionContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    flex: 1,
    minWidth: 0,
    wordBreak: 'break-word',
    whiteSpace: 'normal',
    overflow: 'hidden'
  },
  optionLabel: {
    fontFamily: mixins.fontFamilyPlusSans,
    fontWeight: mixins.fontWeight400,
    fontStyle: 'normal',
    fontSize: mixins.fontSize14,
    lineHeight: '22px',
    color: mixins.color_text_primary,
    whiteSpace: 'normal',
    overflow: 'visible',
    textOverflow: 'initial'
  },
  correctLabel: {
    color: mixins.accentGreen
  },
  wrongLabel: {
    color: mixins.accentRed
  },
  optionSubLabel: {
    fontFamily: mixins.fontFamilyPlusSans,
    fontWeight: mixins.fontWeight400,
    fontStyle: 'normal',
    fontSize: mixins.fontSize12,
    lineHeight: '16px'
  },
  correctSubLabel: {
    color: mixins.accentGreen,
    fontFamily: mixins.fontFamilyPlusSans,
    fontWeight: mixins.fontWeight600,
    fontStyle: 'normal',
    fontSize: mixins.fontSize12,
    lineHeight: '20px'
  },
  wrongSubLabel: {
    color: mixins.accentRed,
    fontFamily: mixins.fontFamilyPlusSans,
    fontWeight: mixins.fontWeight600,
    fontStyle: 'normal',
    fontSize: mixins.fontSize12,
    lineHeight: '20px'
  },
  correct: {
    border: `1px solid ${mixins.accentGreenDark}`,
    background: mixins.accentColor
  },
  wrong: {
    border: `1px solid ${mixins.accentRedDark}`,
    background: mixins.accentLightRed
  },
  optionIcon: {
    justifySelf: 'end'
  },
  statusIcon: {
    width: 18,
    height: 18,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  statusIconCorrect: {
    backgroundColor: mixins.accentGreen
  },
  statusIconWrong: {
    backgroundColor: mixins.accentRed
  },
  optionPrefix: {
    width: 24,
    height: 24,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: mixins.fontFamilyPlusSans,
    fontWeight: mixins.fontWeight600,
    fontStyle: 'normal',
    fontSize: mixins.fontSize14,
    lineHeight: '24px',
    letterSpacing: 0,
    textAlign: 'center',
    border: `1px solid ${mixins.optionPrefixColor}`,
    color: mixins.color_text_primary,
    background: mixins.accentNotAttempted,
    padding: 0,
    opacity: 1,
    boxSizing: 'border-box'
  },
  prefixCorrect: {
    borderColor: mixins.accentGreenDark,
    color: mixins.accentGreen,
    background: mixins.prefixCorrectBGColor
  },
  prefixWrong: {
    borderColor: mixins.accentRedDark,
    color: mixins.accentRed,
    background: mixins.prefixWrongColor
  },
  footer: {
    color: mixins.zambezi,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize12,
    fontWeight: mixins.fontWeight500,
    lineHeight: '20px',
    marginTop: 16,
    display: 'flex',
    alignItems: 'center'
  },
  icon: {
    width: '16px',
    height: '16px'
  },
  questionStem: {
    display: 'flex'
  },
  questionNumberWrapper: {
    display: 'flex'
  },
  questionTitle: {
    color: mixins.color_text_primary,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize20,
    fontWeight: mixins.fontWeight500,
    lineHeight: '28px',
    letterSpacing: '-0.4px'
  },
  primaryCol1: {
    padding: '0px 4px'
  },
  correctOption: {
    border: `1px solid ${mixins.accentGreenDark}`,
    background: mixins.accentColor
  },
  selectedOptions: {
    border: `1px solid ${mixins.accentRedDark}`,
    background: mixins.accentLightRed
  },
  reviewBadge: {
    '& .MuiBadge-badge': {
      borderRadius: 20,
      backgroundColor: mixins.accentLightRed,
      color: mixins.accentRed,
      fontFamily: mixins.fontFamilyPlusSans,
      fontWeight: mixins.fontWeight500,
      fontSize: mixins.fontSize12,
      lineHeight: 14
    }
  }
}));

export default useDifficultTopicStyles;
